import { useEffect, useMemo } from "react";

import { useSaveUserPmPreferenceMutation } from "generated/graphql";
import { PMCODES_CLAIM_KEY } from "shell/platform/constants/constants";
import { useDecodedTokens } from "shell/platform/hooks/useDecodedTokens";
import { useLocalStorage } from "shell/platform/hooks/useLocalStorage";
import { useOrganizationAccess } from "shell/platform/hooks/useOrganizationAccess";
import { getParsedPmidInfo } from "shell/platform/lib/oidcClient";

const PMID_STORAGE_KEY = "active-pmid";
const PMCODE_STORAGE_KEY = "active-pmcode";

export const usePMIDs = () => {
  const [activePMID, setActivePMID] = useLocalStorage(PMID_STORAGE_KEY, "");

  const { idToken, accessToken } = useDecodedTokens();
  const { organisationId, organization } = useOrganizationAccess();
  const [saveUserPmPreference] = useSaveUserPmPreferenceMutation();
  const restrictedPmCodes = idToken?.[PMCODES_CLAIM_KEY];
  const availablePMIDS = useMemo(() => {
    if (!accessToken?.multipmid) {
      return (organization?.pmInformation ?? [])
        .filter(pmInfo => !restrictedPmCodes?.includes(pmInfo.pmCode))
        .map(pmInfo => ({
          pmid: pmInfo.pmId.toString(),
          pmCode: pmInfo.pmCode,
        }));
    }

    return accessToken?.multipmid
      .filter(pmid => pmid.pmid !== null && pmid.pmid !== undefined)
      .map(pmid => {
        if (!restrictedPmCodes?.find(resCode => resCode === pmid.pmcode)) {
          return {
            pmid: pmid.pmid.toString(),
            pmCode: pmid.pmcode,
          };
        }
        return null;
      })
      .filter(Boolean) as Array<{
      pmid: string;
      pmCode: string;
    }>;
  }, [restrictedPmCodes, accessToken?.multipmid, organization?.pmInformation]);

  useEffect(() => {
    if (!activePMID) {
      if (availablePMIDS && availablePMIDS?.length > 0) {
        setActivePMID(availablePMIDS[0].pmid);
        if (availablePMIDS[0].pmCode) {
          globalThis.localStorage.setItem(
            PMCODE_STORAGE_KEY,
            JSON.stringify(availablePMIDS[0].pmCode),
          );
        }
        return;
      }

      if (accessToken?.pmid) {
        setActivePMID(accessToken.pmid.toString());
        const pmCode = availablePMIDS?.find(
          current => current.pmid === accessToken?.pmid?.toString(),
        )?.pmCode;
        if (pmCode) {
          globalThis.localStorage.setItem(
            PMCODE_STORAGE_KEY,
            JSON.stringify(pmCode),
          );
        }
        return;
      }
    } else {
      // If the stored Active PMId is not one the current user can access
      // (e.g. after logging back in as a different user/organisation), reset
      // it to the first available PMId so a stale selection isn't carried over.
      if (
        availablePMIDS &&
        availablePMIDS.length > 0 &&
        !availablePMIDS.some(pm => pm.pmid === activePMID)
      ) {
        const fallback = availablePMIDS[0];
        setActivePMID(fallback.pmid);
        if (fallback.pmCode) {
          globalThis.localStorage.setItem(
            PMCODE_STORAGE_KEY,
            JSON.stringify(fallback.pmCode),
          );
        }
        return;
      }

      // IF - a scenario where the user's Active PMId has been added to the Restricted Codes list (and we're using the ID Token)
      // we want to set the ActivePMId to the first available PMId
      // ELSE - In a scenario where we have an ActivePMId, but the PMId Value changed
      // we need to update the value in local storage so that the user can change PM IDs
      const pmidCodeLS = globalThis.localStorage.getItem(PMCODE_STORAGE_KEY);

      if (activePMID && pmidCodeLS) {
        if (
          restrictedPmCodes?.find(code => pmidCodeLS.includes(code)) &&
          availablePMIDS
        ) {
          globalThis.localStorage.setItem(
            PMID_STORAGE_KEY,
            JSON.stringify(availablePMIDS[0]?.pmid.toString()),
          );
          setActivePMID(availablePMIDS[0]?.pmid.toString());
        } else {
          accessToken?.multipmid?.forEach(currEl => {
            if (
              currEl.pmcode === JSON.parse(pmidCodeLS) &&
              currEl.pmid.toString() !== activePMID
            ) {
              globalThis.localStorage.setItem(
                PMID_STORAGE_KEY,
                JSON.stringify(currEl.pmid.toString()),
              );
              setActivePMID(currEl.pmid.toString());
            }
          });
        }
      }
    }
  }, [
    availablePMIDS,
    activePMID,
    accessToken?.pmid,
    setActivePMID,
    accessToken?.multipmid,
    restrictedPmCodes,
  ]);

  return {
    activePMID,
    availablePMIDs: availablePMIDS ?? [],
    onPMIDChange: (pmid: string) => {
      setActivePMID(pmid);
      const pmCode = availablePMIDS?.find(
        current => current.pmid === pmid,
      )?.pmCode;
      if (pmCode) {
        globalThis.localStorage.setItem(
          PMCODE_STORAGE_KEY,
          JSON.stringify(pmCode),
        );

        if (organisationId) {
          saveUserPmPreference({
            variables: {
              input: {
                organisationId,
                pmcode: pmCode,
                pmid: Number.parseInt(pmid, 10),
              },
            },
          });
        }
      }
    },
    getValue: () => {
      try {
        const parsed = getParsedPmidInfo();
        return availablePMIDS?.find(pmid => pmid.pmid === parsed.pmid);
      } catch {
        return;
      }
    },
  };
};
